import * as React from "react";
import * as DialogPrimitive from "@radix-ui/react-dialog";
import { X } from "lucide-react";
import { cn } from "@/lib/utils";
import { useDraggable } from "@/hooks/use-draggable";
import { DialogPortal, DialogOverlay } from "@/components/ui/dialog";

/**
 * DraggableDialogContent
 *
 * Drop-in replacement for DialogContent that makes the modal draggable.
 * The first <DialogHeader> child automatically becomes the drag handle
 * (cursor changes to "move", onMouseDown wired up).
 *
 * Layout: flex-column with three sections:
 *   1. Header  — non-scrollable, sticky at top   (drag handle)
 *   2. Body    — scrollable, grows to fill space
 *   3. Footer  — non-scrollable, sticky at bottom
 *
 * Supports a `size` prop for width presets:
 *   "sm"   → max-w-sm   (~384px)
 *   "md"   → max-w-lg   (~512px)  ← default
 *   "lg"   → max-w-2xl  (~672px)
 *   "xl"   → max-w-4xl  (~896px)
 *   "full" → max-w-[90vw]
 */

const sizeClasses = {
  sm:   "tw:max-w-sm",
  md:   "tw:max-w-lg",
  lg:   "tw:max-w-2xl",
  xl:   "tw:max-w-4xl",
  full: "tw:max-w-[90vw]",
};

const DraggableDialogContent = React.forwardRef(
  ({ className, children, size = "md", onCloseAutoFocus, onPointerDownOutside: onPointerDownOutsideProp, style: externalStyle, zIndex, overlayClassName, ...props }, ref) => {
    const { position, handleMouseDown, resetPosition } = useDraggable();

    // Separate children into header, body, and footer sections
    let headerChild = null;
    let footerChild = null;
    const bodyChildren = [];

    React.Children.forEach(children, (child) => {
      if (!React.isValidElement(child)) {
        bodyChildren.push(child);
        return;
      }
      if (child.type?.displayName === "DialogHeader") {
        // Inject drag handle onto the header
        headerChild = React.cloneElement(child, {
          onMouseDown: handleMouseDown,
          className: cn("tw:cursor-move tw:select-none", child.props.className),
        });
      } else if (child.type?.displayName === "DialogFooter") {
        footerChild = child;
      } else {
        bodyChildren.push(child);
      }
    });

    return (
      <DialogPortal>
        <DialogOverlay className={overlayClassName} />
        <DialogPrimitive.Content
          ref={ref}
          style={{
            animation: 'none',
            ...externalStyle,
            ...(zIndex != null ? { zIndex } : {}),
            // transform must always be last so dragging + centering are never overwritten
            transform: `translate(calc(-50% + ${position.x}px), calc(-50% + ${position.y}px))`,
          }}
          onCloseAutoFocus={(e) => {
            resetPosition();
            onCloseAutoFocus?.(e);
          }}
          onPointerDownOutside={(e) => {
            // Prevent dialog from closing when clicking portaled react-select menus
            const target = e.detail?.originalEvent?.target;
            if (target?.closest?.('[class*="select__"]')) {
              e.preventDefault();
              return;
            }
            onPointerDownOutsideProp?.(e);
          }}
          className={cn(
            // Base positioning
            "tw:fixed tw:left-[50%] tw:top-[50%] tw:z-50",
            // Flex column so header/footer are sticky, body scrolls
            "tw:flex tw:flex-col tw:w-full",
            // Cap height at 90% of viewport
            "tw:max-h-[90vh]",
            // Width preset
            sizeClasses[size] ?? sizeClasses.md,
            // Appearance
            "tw:border tw:border-gray-200 tw:bg-white tw:shadow-modal tw:sm:rounded-card",
            className
          )}
          {...props}
        >
          {/* Non-scrollable header (drag handle) */}
          {headerChild && (
            <div className="tw:flex-shrink-0 tw:px-6 tw:pt-6 tw:pb-3">
              {headerChild}
            </div>
          )}

          {/* Scrollable body */}
          <div className={cn(
            "tw:flex-1 tw:min-h-0 tw:overflow-y-auto tw:px-6 tw:flex tw:flex-col tw:gap-2",
            !headerChild ? "tw:pt-6" : "tw:pt-1",
            !footerChild ? "tw:pb-6" : "tw:pb-3",
          )}>
            {bodyChildren}
          </div>

          {/* Non-scrollable footer */}
          {footerChild && (
            <div className="tw:flex-shrink-0 tw:px-6 tw:pt-3 tw:pb-6">
              {footerChild}
            </div>
          )}

          {/* Close button — absolutely positioned, always visible */}
          <DialogPrimitive.Close className="tw:absolute tw:right-4 tw:top-4 tw:rounded-sm tw:opacity-70 tw:ring-offset-white tw:transition-opacity tw:hover:opacity-100 tw:focus:outline-none tw:focus:ring-2 tw:focus:ring-brand-secondary tw:focus:ring-offset-2 tw:disabled:pointer-events-none">
            <X className="tw:h-4 tw:w-4" />
            <span className="tw:sr-only">Close</span>
          </DialogPrimitive.Close>
        </DialogPrimitive.Content>
      </DialogPortal>
    );
  }
);
DraggableDialogContent.displayName = "DraggableDialogContent";

export { DraggableDialogContent };
